/**
 * Google Ads conversion tracking (AW-11173581974). Labels and values come from
 * the marketing spec «Google Ads Conversion-Tracking» (Stand 29.09.2026); the
 * consent default and `config` are queued in `src/index.html`.
 *
 * Every conversion except the key page view is mirrored as a GA4 event on the
 * Firebase Analytics stream (`page_view` covers that one). GA4 events go out on
 * every environment, since stage/dev report to their own property, and respect
 * the user's analytics opt-out (`setAnalyticsCollectionEnabled`).
 *
 * Commands are pushed straight into the dataLayer instead of going through
 * `window.gtag`: the Firebase Analytics SDK replaces that with a wrapper that drops the
 * third argument of `set`, which would lose `user_data`.
 */

import { environment } from '../../environments/environment';
import {
  Ga4Event,
  Ga4Param,
  LegacyGa4Event,
  PurchaseFlow,
  PurchasePlan,
} from './ga4-events';

const ADS_ID = 'AW-11173581974';
const GA4_ID = environment.firebase.measurementId;

/** Only the production app counts; stage/dev/e2e never send conversions. */
const TRACKING_HOST = 'app.bildigsnetz.ch';

/** Default value for the subscription purchase when the amount is unknown. */
const DEFAULT_PURCHASE_VALUE_CHF = 588;

const CONVERSIONS = {
  purchase: 'VBRJCI7WzYodEJap_c8p',
  signupSchool: 'Vc1FCJHWzYodEJap_c8p',
  signupProfessional: '2YK0CJTWzYodEJap_c8p',
  newsletter: 'nEzDCJfWzYodEJap_c8p',
  jobAlert: 'OwitCJrWzYodEJap_c8p',
  keyPageView: 'CVdHCKDWzYodEJap_c8p',
} as const;

/** Paths that count as key page views (Stellenportal). */
const KEY_PAGES = [/^\/jobs\/?$/];

export type SignupRole = 'school' | 'professional';

function pushToDataLayer(args: IArguments): void {
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  // gtag.js expects the Arguments object, not an array.
  w.dataLayer.push(args);
}

/** Google Ads command; production host only. */
function gtag(..._args: unknown[]): void {
  if (window.location.hostname !== TRACKING_HOST) return;
  // eslint-disable-next-line prefer-rest-params
  pushToDataLayer(arguments);
}

/** GA4 command; every environment. */
function ga4(..._args: unknown[]): void {
  // eslint-disable-next-line prefer-rest-params
  pushToDataLayer(arguments);
}

function sendGa4Event(name: string, params: Record<string, unknown>): void {
  ga4('event', name, { send_to: GA4_ID, ...params });
}

function setUserEmail(email: string | null | undefined): void {
  if (email) gtag('set', 'user_data', { email });
}

function sendConversion(
  label: string,
  value: number,
  extra: Record<string, unknown> = {}
): void {
  gtag('event', 'conversion', {
    send_to: `${ADS_ID}/${label}`,
    value,
    currency: 'CHF',
    ...extra,
  });
}

/**
 * Account created and role fixed. Call once per user, right after the
 * create-profile request succeeded (the backend creates a profile only once).
 */
export function trackSignupConversion(
  role: SignupRole,
  email: string | null | undefined,
  newsletterOptIn: boolean
): void {
  setUserEmail(email);
  const value = role === 'school' ? 80 : 15;
  sendConversion(
    role === 'school'
      ? CONVERSIONS.signupSchool
      : CONVERSIONS.signupProfessional,
    value
  );
  sendGa4Event('sign_up', {
    [Ga4Param.accountType]: role === 'school' ? 'schule' : 'fachkraft',
    account_type: role,
    value,
    currency: 'CHF',
  });
  if (newsletterOptIn) trackNewsletterConversion();
}

/** Newsletter consent saved (signup checkbox or marketing settings). */
export function trackNewsletterConversion(): void {
  sendConversion(CONVERSIONS.newsletter, 5);
  sendGa4Event(Ga4Event.newsletterSubscribed, { value: 5, currency: 'CHF' });
  sendGa4Event(LegacyGa4Event.newsletterSignup, { value: 5, currency: 'CHF' });
}

/** Job alert (Stellenabo) switched on and saved. */
export function trackJobAlertConversion(): void {
  sendConversion(CONVERSIONS.jobAlert, 5);
  sendGa4Event(Ga4Event.jobAlertActivated, { value: 5, currency: 'CHF' });
  sendGa4Event(LegacyGa4Event.jobAlertActivated, { value: 5, currency: 'CHF' });
}

/**
 * School/authority subscription ordered successfully. `valueChf` is the net
 * amount after discounts; `transactionId` must be unique per order so Google
 * Ads deduplicates replays. `flow`, `plan` and `freeStart` tell new customers
 * from upgrades and negotiated offers in GA4; a free start still counts as a
 * purchase, with the amount that will be billed.
 */
export function trackPurchaseConversion(params: {
  valueChf: number | null | undefined;
  transactionId: string;
  email: string | null | undefined;
  flow: PurchaseFlow;
  plan: PurchasePlan;
  freeStart: boolean;
}): void {
  setUserEmail(params.email);
  const value =
    params.valueChf != null && Number.isFinite(params.valueChf)
      ? Math.round(params.valueChf * 100) / 100
      : DEFAULT_PURCHASE_VALUE_CHF;
  sendConversion(CONVERSIONS.purchase, value, {
    transaction_id: params.transactionId,
  });
  sendGa4Event('purchase', {
    transaction_id: params.transactionId,
    value,
    currency: 'CHF',
    [Ga4Param.purchaseFlow]: params.flow,
    [Ga4Param.plan]: params.plan,
    [Ga4Param.freeStart]: params.freeStart ? 'ja' : 'nein',
  });
}

/** Key page view; call on every navigation (client-side routing included). */
export function trackKeyPageView(url: string): void {
  const path = url.split(/[?#]/)[0];
  if (KEY_PAGES.some(r => r.test(path))) {
    sendConversion(CONVERSIONS.keyPageView, 0.5);
  }
}

/** Total contract value of a negotiated offer (Kanton-Tarif), in CHF. */
export function negotiatedOfferValueChf(offer: {
  yearly_price_rappen: number;
  contract_years: number;
}): number {
  return (offer.yearly_price_rappen * Math.max(1, offer.contract_years)) / 100;
}
